import React, { useState, useEffect, useRef } from 'react';
import { Check, X, GripVertical, Lightbulb, Award, Pencil, Music, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

// =============== MATCHING PAIRS INTERACTION ===============
export const MatchingPairs = ({ block, onComplete }) => {
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [matches, setMatches] = useState({});
  const [shuffledRight, setShuffledRight] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [incorrectMatches, setIncorrectMatches] = useState({}); // Track which matches are wrong
  const [showHint, setShowHint] = useState(false);
  const [hintText, setHintText] = useState('');
  
  const columnA = block.column_a || [];
  const columnB = block.column_b || [];
  
  useEffect(() => {
    // Shuffle column B on mount
    const shuffled = [...columnB].sort(() => Math.random() - 0.5);
    setShuffledRight(shuffled);
  }, [columnB.join(',')]);
  
  const handleLeftClick = (idx) => {
    if (completed) return;
    setSelectedLeft(idx);
  };
  
  const handleRightClick = (rightItem) => {
    if (selectedLeft === null || completed) return;
    
    // Clear error state for this item
    setIncorrectMatches(prev => {
      const newState = { ...prev };
      delete newState[selectedLeft];
      return newState;
    });
    setShowHint(false);
    
    // Record the match
    const newMatches = {
      ...matches,
      [selectedLeft]: rightItem
    };
    setMatches(newMatches);
    setSelectedLeft(null);
    
    // Check if all matched
    if (Object.keys(newMatches).length === columnA.length) {
      // Find which matches are incorrect
      const wrongMatches = {};
      columnA.forEach((item, idx) => {
        const matchedItem = newMatches[idx];
        const correctAnswer = columnB[idx];
        if (matchedItem !== correctAnswer) {
          wrongMatches[idx] = matchedItem;
        }
      });
      
      setTimeout(() => {
        if (Object.keys(wrongMatches).length === 0) {
          toast.success('All matches correct! 🎉');
          setCompleted(true);
          onComplete && onComplete(true);
        } else {
          // Highlight incorrect matches
          setIncorrectMatches(wrongMatches);
          setHintText(generateMatchHint(wrongMatches, columnA));
          setShowHint(true);
          toast.error(`${Object.keys(wrongMatches).length} match${Object.keys(wrongMatches).length > 1 ? 'es are' : ' is'} incorrect. Try again!`);
          // Keep matches but allow re-matching
        }
      }, 300);
    }
  };
  
  const generateMatchHint = (wrongMatches, columnA) => {
    const hints = [
      "Look carefully at each pair - do they truly belong together?",
      "Think about the meaning of each item on the left side.",
      "Consider what makes a good match for each concept.",
      "The highlighted pairs need a second look - what connects them?"
    ];
    
    if (block.hints && block.hints.length > 0) {
      return block.hints[Math.floor(Math.random() * block.hints.length)];
    }
    
    return hints[Math.floor(Math.random() * hints.length)];
  };
  
  // Allow re-matching incorrect items
  const handleRematch = (leftIdx) => {
    if (completed || !incorrectMatches[leftIdx]) return;
    
    // Remove the incorrect match
    const matchedItem = matches[leftIdx];
    setMatches(prev => {
      const newMatches = { ...prev };
      delete newMatches[leftIdx];
      return newMatches;
    });
    setIncorrectMatches(prev => {
      const newState = { ...prev };
      delete newState[leftIdx];
      return newState;
    });
    setShowHint(false);
    setSelectedLeft(leftIdx);
  };
  
  const isMatched = (leftIdx) => matches[leftIdx] !== undefined;
  const isRightMatched = (rightItem) => Object.values(matches).includes(rightItem);
  const isIncorrect = (leftIdx) => incorrectMatches[leftIdx] !== undefined;
  
  return (
    <div className="space-y-6">
      <p className="text-lg text-slate-200 mb-4">{block.content || 'Match the items from Column A with Column B'}</p>
      
      {/* Hint display */}
      {showHint && hintText && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
          <Lightbulb className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-300 font-medium mb-1">Hint</p>
            <p className="text-slate-300 text-sm">{hintText}</p>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-8">
        {/* Column A */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-indigo-400 mb-2">Column A</h4>
          {columnA.map((item, idx) => (
            <button
              key={idx}
              onClick={() => isIncorrect(idx) ? handleRematch(idx) : !isMatched(idx) && handleLeftClick(idx)}
              disabled={isMatched(idx) && !isIncorrect(idx)}
              className={`w-full text-left p-4 rounded-xl transition-all ${
                isIncorrect(idx)
                  ? 'bg-red-900/30 border-2 border-red-500 animate-shake cursor-pointer'
                  : isMatched(idx)
                    ? 'bg-green-900/30 border-2 border-green-500/50 opacity-60'
                    : selectedLeft === idx
                      ? 'bg-indigo-500/30 border-2 border-indigo-400 scale-105'
                      : 'bg-slate-800/50 border-2 border-slate-700 hover:border-indigo-500/50'
              }`}
            >
              <span className="text-white">{item}</span>
              {isMatched(idx) && !isIncorrect(idx) && <Check className="inline-block ml-2 w-4 h-4 text-green-400" />}
              {isIncorrect(idx) && <X className="inline-block ml-2 w-4 h-4 text-red-400" />}
              {isIncorrect(idx) && <span className="text-xs text-red-300 ml-2">(click to rematch)</span>}
            </button>
          ))}
        </div>
        
        {/* Column B */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-purple-400 mb-2">Column B</h4>
          {shuffledRight.map((item, idx) => {
            const isWrongMatch = Object.values(incorrectMatches).includes(item);
            return (
              <button
                key={idx}
                onClick={() => selectedLeft !== null && (!isRightMatched(item) || isWrongMatch) && handleRightClick(item)}
                disabled={isRightMatched(item) && !isWrongMatch}
                className={`w-full text-left p-4 rounded-xl transition-all ${
                  isWrongMatch
                    ? 'bg-red-900/30 border-2 border-red-500 animate-shake'
                    : isRightMatched(item)
                      ? 'bg-green-900/30 border-2 border-green-500/50 opacity-60'
                      : selectedLeft !== null
                        ? 'bg-slate-800/50 border-2 border-purple-500/50 hover:bg-purple-500/20 cursor-pointer'
                        : 'bg-slate-800/50 border-2 border-slate-700 opacity-70'
                }`}
              >
                <span className="text-white">{item}</span>
                {isRightMatched(item) && !isWrongMatch && <Check className="inline-block ml-2 w-4 h-4 text-green-400" />}
                {isWrongMatch && <X className="inline-block ml-2 w-4 h-4 text-red-400" />}
              </button>
            );
          })}
        </div>
      </div>
      
      {selectedLeft !== null && (
        <p className="text-sm text-indigo-300 text-center animate-pulse">
          Now click on the matching item in Column B
        </p>
      )}
    </div>
  );
};

// =============== DRAG & DROP ORDERING ===============
export const DragDropOrder = ({ block, onComplete }) => {
  const correctOrder = block.items_to_order || block.options || [];
  const [items, setItems] = useState([]);
  const [draggedIdx, setDraggedIdx] = useState(null);
  
  useEffect(() => {
    // Shuffle items on mount
    setItems([...correctOrder].sort(() => Math.random() - 0.5));
  }, []);
  
  const handleDragStart = (idx) => {
    setDraggedIdx(idx);
  };
  
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    
    const newItems = [...items];
    const draggedItem = newItems[draggedIdx];
    newItems.splice(draggedIdx, 1);
    newItems.splice(idx, 0, draggedItem);
    setItems(newItems);
    setDraggedIdx(idx);
  };
  
  const handleDragEnd = () => {
    setDraggedIdx(null);
  };
  
  const handleCheck = () => {
    const isCorrect = items.every((item, idx) => item === correctOrder[idx]);
    if (isCorrect) {
      toast.success('Perfect order!');
      onComplete && onComplete(true);
    } else {
      toast.error('Not quite right. Try again!');
    }
  };
  
  return (
    <div className="space-y-6">
      <p className="text-lg text-slate-200 mb-4">{block.content || 'Drag items to put them in the correct order'}</p>
      
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div
            key={`${item}-${idx}`}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-3 p-4 rounded-xl cursor-move transition-all ${
              draggedIdx === idx
                ? 'bg-indigo-500/30 border-2 border-indigo-400 scale-105 shadow-lg'
                : 'bg-slate-800/50 border-2 border-slate-700 hover:border-indigo-500/30'
            }`}
          >
            <GripVertical className="w-5 h-5 text-slate-500" />
            <span className="text-sm text-slate-400 font-mono">{idx + 1}.</span>
            <span className="text-white flex-1">{item}</span>
          </div>
        ))}
      </div>
      
      <button
        onClick={handleCheck}
        className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all"
      >
        Check Order
      </button>
    </div>
  );
};

// =============== SORTING / CATEGORIZING ===============
export const SortingActivity = ({ block, onComplete }) => {
  const categories = block.categories || [];
  const sortingItems = block.sorting_items || [];
  const [placements, setPlacements] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [incorrectItems, setIncorrectItems] = useState([]); // Track which items are wrong
  const [showHint, setShowHint] = useState(false);
  const [hintText, setHintText] = useState('');
  
  useEffect(() => {
    // Shuffle items - handle both 'item' and 'text' keys for compatibility
    const items = sortingItems.map(si => si.item || si.text || si);
    setAvailableItems([...items].sort(() => Math.random() - 0.5));
  }, []);
  
  const handleDrop = (category, item) => {
    // Clear previous error state when item is moved
    setIncorrectItems(prev => prev.filter(i => i !== item));
    setShowHint(false);
    
    setPlacements(prev => ({
      ...prev,
      [item]: category
    }));
    setAvailableItems(prev => prev.filter(i => i !== item));
  };
  
  // Allow items to be moved back from categories
  const handleRemoveFromCategory = (item) => {
    setPlacements(prev => {
      const newPlacements = { ...prev };
      delete newPlacements[item];
      return newPlacements;
    });
    setAvailableItems(prev => [...prev, item]);
    setIncorrectItems(prev => prev.filter(i => i !== item));
    setShowHint(false);
  };
  
  const generateHint = (wrongItems) => {
    // Generate contextual hints based on wrong items
    const hints = [
      "Think about what category fits best for each item.",
      "Consider what makes each item unique or special.",
      "Look at the highlighted items and ask yourself: where do they really belong?",
      "Take a moment to reconsider - which group feels most natural for each item?",
      "Try thinking about each item's main purpose or meaning."
    ];
    
    // Get specific hints from block if available
    if (block.hints && block.hints.length > 0) {
      return block.hints[Math.floor(Math.random() * block.hints.length)];
    }
    
    return hints[Math.floor(Math.random() * hints.length)];
  };
  
  const handleCheck = () => {
    const allPlaced = availableItems.length === 0;
    if (!allPlaced) {
      toast.error('Please sort all items first!');
      return;
    }
    
    // Find which items are incorrect - handle both 'item' and 'text' keys
    const wrongItems = sortingItems
      .filter(si => {
        const item = si.item || si.text || si;
        const correctCategory = si.category;
        return placements[item] !== correctCategory;
      })
      .map(si => si.item || si.text || si);
    
    if (wrongItems.length === 0) {
      toast.success('All items sorted correctly! 🎉');
      setIncorrectItems([]);
      setShowHint(false);
      onComplete && onComplete(true);
    } else {
      // Highlight incorrect items
      setIncorrectItems(wrongItems);
      setHintText(generateHint(wrongItems));
      setShowHint(true);
      toast.error(`${wrongItems.length} item${wrongItems.length > 1 ? 's are' : ' is'} in the wrong place. Try again!`);
    }
  };
  
  const getCategoryItems = (category) => {
    return Object.entries(placements)
      .filter(([_, cat]) => cat === category)
      .map(([item, _]) => item);
  };
  
  const isItemIncorrect = (item) => incorrectItems.includes(item);
  
  return (
    <div className="space-y-6">
      <p className="text-lg text-slate-200 mb-4">{block.content || 'Sort the items into the correct categories'}</p>
      
      {/* Hint display */}
      {showHint && hintText && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
          <Lightbulb className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-300 font-medium mb-1">Hint</p>
            <p className="text-slate-300 text-sm">{hintText}</p>
          </div>
        </div>
      )}
      
      {/* Available items */}
      {availableItems.length > 0 && (
        <div className="bg-slate-900/50 p-4 rounded-xl">
          <h4 className="text-sm font-semibold text-slate-400 mb-3">Items to sort:</h4>
          <div className="flex flex-wrap gap-2">
            {availableItems.map((item, idx) => (
              <div
                key={idx}
                draggable
                onDragStart={(e) => e.dataTransfer.setData('item', item)}
                className="bg-indigo-500/20 border border-indigo-500/50 text-white px-4 py-2 rounded-lg cursor-move hover:bg-indigo-500/30 transition-all"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Categories */}
      <div className="grid grid-cols-2 gap-4">
        {categories.map((category, idx) => (
          <div
            key={idx}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const item = e.dataTransfer.getData('item');
              if (item) handleDrop(category, item);
            }}
            className="bg-slate-800/30 border-2 border-dashed border-slate-600 rounded-xl p-4 min-h-[150px]"
          >
            <h4 className="text-sm font-semibold text-purple-400 mb-3">{category}</h4>
            <div className="flex flex-wrap gap-2">
              {getCategoryItems(category).map((item, i) => (
                <button
                  key={i}
                  onClick={() => handleRemoveFromCategory(item)}
                  className={`px-3 py-1 rounded-lg text-sm transition-all cursor-pointer ${
                    isItemIncorrect(item)
                      ? 'bg-red-500/30 border-2 border-red-500 text-red-300 animate-shake'
                      : 'bg-green-500/20 border border-green-500/50 text-green-300 hover:bg-green-500/30'
                  }`}
                  title="Click to move back"
                >
                  {item}
                  {isItemIncorrect(item) && <X className="inline-block ml-1 w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <button
        onClick={handleCheck}
        className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all"
      >
        Check Sorting
      </button>
    </div>
  );
};

// =============== FILL IN THE BLANK ===============
export const FillInBlank = ({ block, onComplete }) => {
  const [answer, setAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  
  const content = block.content || '';
  const correctAnswers = (block.correct_answer || '').split(',').map(a => a.trim().toLowerCase());
  
  // Split content by ___ to show blanks
  const parts = content.split('___');
  
  const handleCheck = () => {
    const userAnswer = answer.trim().toLowerCase();
    const correct = correctAnswers.includes(userAnswer);
    setIsCorrect(correct);
    setShowResult(true);
    
    if (correct) {
      toast.success('Correct!');
      onComplete && onComplete(true);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="text-lg text-slate-200 leading-relaxed">
        {parts.map((part, idx) => (
          <span key={idx}>
            {part}
            {idx < parts.length - 1 && (
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={showResult && isCorrect}
                className={`inline-block mx-2 px-3 py-1 w-32 text-center rounded-lg border-2 bg-slate-900/50 ${
                  showResult
                    ? isCorrect
                      ? 'border-green-500 text-green-300'
                      : 'border-red-500 text-red-300'
                    : 'border-indigo-500 text-white focus:ring-2 focus:ring-indigo-500/50'
                }`}
                placeholder="..."
              />
            )}
          </span>
        ))}
      </div>
      
      {showResult && !isCorrect && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
          <p className="text-red-300">
            <X className="inline w-4 h-4 mr-2" />
            Not quite. Hint: {block.hint || `The answer is one of: ${correctAnswers.join(', ')}`}
          </p>
        </div>
      )}
      
      {!showResult && (
        <button
          onClick={handleCheck}
          disabled={!answer.trim()}
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
        >
          Check Answer
        </button>
      )}
      
      {showResult && !isCorrect && (
        <button
          onClick={() => { setShowResult(false); setAnswer(''); }}
          className="w-full bg-slate-700 text-white rounded-full px-6 py-3 font-semibold hover:bg-slate-600 transition-all"
        >
          Try Again
        </button>
      )}
    </div>
  );
};

// =============== QUICK RECALL ===============
export const QuickRecall = ({ block, onComplete }) => {
  const [answer, setAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  
  const acceptableAnswers = block.options || [];
  
  const handleCheck = () => {
    const userAnswer = answer.trim().toLowerCase();
    const correct = acceptableAnswers.some(a => 
      a.toLowerCase() === userAnswer || 
      userAnswer.includes(a.toLowerCase())
    );
    setIsCorrect(correct);
    setShowResult(true);
    
    if (correct) {
      toast.success('Well remembered!');
      onComplete && onComplete(true);
    }
  };
  
  return (
    <div className="space-y-6">
      <p className="text-lg text-slate-200 mb-4">{block.content}</p>
      
      <textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        disabled={showResult && isCorrect}
        rows={3}
        className="w-full bg-slate-900/50 border-2 border-slate-700 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 transition-all"
        placeholder="Type your answer..."
      />
      
      {showResult && (
        <div className={`p-4 rounded-xl ${isCorrect ? 'bg-green-900/20 border border-green-500/30' : 'bg-yellow-900/20 border border-yellow-500/30'}`}>
          {isCorrect ? (
            <p className="text-green-300">
              <Check className="inline w-4 h-4 mr-2" />
              Excellent memory!
            </p>
          ) : (
            <p className="text-yellow-300">
              <Lightbulb className="inline w-4 h-4 mr-2" />
              Good try! The key points were: {acceptableAnswers.slice(0, 3).join(', ')}
            </p>
          )}
        </div>
      )}
      
      {!showResult && (
        <button
          onClick={handleCheck}
          disabled={!answer.trim()}
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
        >
          Check Recall
        </button>
      )}
      
      {showResult && !isCorrect && (
        <button
          onClick={() => onComplete && onComplete(true)}
          className="w-full bg-slate-700 text-white rounded-full px-6 py-3 font-semibold hover:bg-slate-600 transition-all"
        >
          Continue
        </button>
      )}
    </div>
  );
};

// =============== LOGIC PUZZLE / CHALLENGE ===============
export const LogicPuzzle = ({ block, onComplete }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [attempts, setAttempts] = useState(0);
  
  const options = block.options || [];
  const correctAnswer = block.correct_answer;
  
  const handleCheck = () => {
    const isCorrect = selectedOption === correctAnswer;
    setAttempts(prev => prev + 1);
    setShowResult(true);
    
    if (isCorrect) {
      toast.success('Brilliant thinking!');
      onComplete && onComplete(true);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <Award className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
          <p className="text-lg text-slate-200 whitespace-pre-wrap">{block.content}</p>
        </div>
      </div>
      
      <div className="space-y-3">
        {options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => !showResult && setSelectedOption(option)}
            disabled={showResult && selectedOption === correctAnswer}
            className={`w-full text-left p-4 rounded-xl transition-all ${
              showResult && option === correctAnswer
                ? 'bg-green-500/30 border-2 border-green-500'
                : showResult && option === selectedOption && option !== correctAnswer
                  ? 'bg-red-500/30 border-2 border-red-500'
                  : selectedOption === option
                    ? 'bg-indigo-500/30 border-2 border-indigo-400'
                    : 'bg-slate-800/50 border-2 border-slate-700 hover:border-indigo-500/30'
            }`}
          >
            <span className="text-white">{option}</span>
          </button>
        ))}
      </div>
      
      {showResult && selectedOption !== correctAnswer && (
        <div className="bg-slate-800/50 border border-slate-600 rounded-xl p-4">
          <p className="text-slate-300">
            {attempts === 1 && block.hint && (
              <>
                <Lightbulb className="inline w-4 h-4 mr-2 text-yellow-400" />
                Hint: {block.hint}
              </>
            )}
            {attempts >= 2 && block.explanation && (
              <>
                <BookOpen className="inline w-4 h-4 mr-2 text-blue-400" />
                {block.explanation}
              </>
            )}
          </p>
        </div>
      )}
      
      {!showResult && (
        <button
          onClick={handleCheck}
          disabled={!selectedOption}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
        >
          Submit Answer
        </button>
      )}
      
      {showResult && selectedOption !== correctAnswer && (
        <button
          onClick={() => { setShowResult(false); setSelectedOption(null); }}
          className="w-full bg-slate-700 text-white rounded-full px-6 py-3 font-semibold hover:bg-slate-600 transition-all"
        >
          Try Again
        </button>
      )}
    </div>
  );
};

// =============== CREATIVE WRITING ===============
export const CreativeWriting = ({ block, onComplete }) => {
  const [text, setText] = useState('');
  const minWords = block.min_words || 10;
  
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const canSubmit = wordCount >= minWords;
  
  const writingTypes = {
    story: 'Short Story',
    poem: 'Poem',
    description: 'Description',
    letter: 'Letter',
    journal: 'Journal Entry'
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/30 rounded-xl p-6">
        <div className="flex items-start gap-3 mb-2">
          <Pencil className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-amber-300 font-semibold mb-1">
              {writingTypes[block.writing_type] || 'Creative Writing'}
            </h4>
            <p className="text-slate-200">{block.content}</p>
          </div>
        </div>
      </div>
      
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        className="w-full bg-slate-900/50 border-2 border-slate-700 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:border-amber-500/50 transition-all"
        placeholder="Let your creativity flow..."
      />
      
      <div className="flex items-center justify-between text-sm">
        <span className={`${canSubmit ? 'text-green-400' : 'text-slate-400'}`}>
          {wordCount} / {minWords} words {canSubmit && <Check className="inline w-4 h-4 ml-1" />}
        </span>
        {block.example_image && (
          <span className="text-slate-500">Inspiration image available</span>
        )}
      </div>
      
      <button
        onClick={() => {
          toast.success('Beautiful creation saved!');
          onComplete && onComplete(true, { text });
        }}
        disabled={!canSubmit}
        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
      >
        Submit My Writing
      </button>
    </div>
  );
};

// =============== DRAWING ACTIVITY ===============
export const DrawingActivity = ({ block, onComplete }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(5);
  
  const colors = ['#000000', '#EF4444', '#F59E0B', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#FFFFFF'];
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    // Set white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);
  
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    ctx.strokeStyle = currentColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    setIsDrawing(true);
    setHasDrawn(true);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-pink-900/20 to-rose-900/20 border border-pink-500/30 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Pencil className="w-6 h-6 text-pink-400 flex-shrink-0 mt-1" />
          <p className="text-slate-200">{block.content}</p>
        </div>
      </div>
      
      {block.example_image && (
        <div className="bg-slate-800/30 rounded-xl p-4">
          <p className="text-sm text-slate-400 mb-2">Inspiration:</p>
          <img src={block.example_image} alt="Inspiration" className="rounded-lg max-h-40 mx-auto" />
        </div>
      )}
      
      {/* Drawing tools */}
      <div className="flex flex-wrap items-center gap-4 p-3 bg-slate-800/50 rounded-xl">
        <div className="flex gap-2">
          {colors.map(color => (
            <button
              key={color}
              onClick={() => setCurrentColor(color)}
              className={`w-8 h-8 rounded-full border-2 transition-transform ${
                currentColor === color ? 'border-white scale-110' : 'border-slate-600'
              }`}
              style={{ backgroundColor: color }}
              data-testid={`color-${color}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">Size:</span>
          <input
            type="range"
            min="2"
            max="20"
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            className="w-20"
          />
        </div>
        <button
          onClick={clearCanvas}
          className="px-3 py-1 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 transition-colors"
        >
          Clear
        </button>
      </div>
      
      {/* Drawing canvas */}
      <canvas
        ref={canvasRef}
        width={600}
        height={400}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        className="w-full bg-white rounded-xl cursor-crosshair touch-none"
        style={{ aspectRatio: '3/2' }}
        data-testid="drawing-canvas"
      />
      
      <button
        onClick={() => {
          toast.success('Wonderful artwork!');
          onComplete && onComplete(true);
        }}
        disabled={!hasDrawn}
        className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
        data-testid="drawing-done-btn"
      >
        I'm Done Drawing
      </button>
    </div>
  );
};

// =============== MUSIC / RHYTHM ACTIVITY ===============
export const MusicRhythm = ({ block, onComplete }) => {
  const [beats, setBeats] = useState([]);
  const beatTypes = ['BOOM', 'tap', 'clap', 'rest'];
  
  const addBeat = (type) => {
    if (beats.length < 16) {
      setBeats([...beats, type]);
    }
  };
  
  const clearBeats = () => setBeats([]);
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-violet-900/20 to-purple-900/20 border border-violet-500/30 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Music className="w-6 h-6 text-violet-400 flex-shrink-0 mt-1" />
          <div>
            <p className="text-slate-200">{block.content}</p>
            {block.beat_hint && (
              <p className="text-sm text-violet-300 mt-2">Hint: {block.beat_hint}</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Beat display */}
      <div className="bg-slate-900/50 rounded-xl p-4 min-h-[60px] flex flex-wrap gap-2 items-center">
        {beats.length === 0 ? (
          <span className="text-slate-500">Click buttons below to create your rhythm...</span>
        ) : (
          beats.map((beat, idx) => (
            <span 
              key={idx} 
              className={`px-3 py-1 rounded-lg text-sm font-mono ${
                beat === 'BOOM' ? 'bg-red-500/30 text-red-300 font-bold' :
                beat === 'tap' ? 'bg-blue-500/30 text-blue-300' :
                beat === 'clap' ? 'bg-yellow-500/30 text-yellow-300' :
                'bg-slate-700/50 text-slate-400'
              }`}
            >
              {beat}
            </span>
          ))
        )}
      </div>
      
      {/* Beat buttons */}
      <div className="flex gap-2 justify-center">
        {beatTypes.map(type => (
          <button
            key={type}
            onClick={() => addBeat(type)}
            className={`px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105 ${
              type === 'BOOM' ? 'bg-red-500/20 text-red-300 border border-red-500/50' :
              type === 'tap' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50' :
              type === 'clap' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50' :
              'bg-slate-700/50 text-slate-300 border border-slate-600'
            }`}
          >
            {type}
          </button>
        ))}
      </div>
      
      <div className="flex gap-3">
        <button
          onClick={clearBeats}
          className="flex-1 bg-slate-700 text-white rounded-full px-6 py-3 font-semibold hover:bg-slate-600 transition-all"
        >
          Clear
        </button>
        <button
          onClick={() => {
            toast.success('Great rhythm!');
            onComplete && onComplete(true, { beats });
          }}
          disabled={beats.length < 4}
          className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
        >
          Submit Rhythm
        </button>
      </div>
    </div>
  );
};

// =============== REFLECTION ACTIVITY ===============
export const ReflectionActivity = ({ block, onComplete }) => {
  const [responses, setResponses] = useState({});
  const questions = block.guiding_questions || ['What did you learn?'];
  
  const updateResponse = (idx, value) => {
    setResponses(prev => ({ ...prev, [idx]: value }));
  };
  
  const allAnswered = questions.every((_, idx) => responses[idx]?.trim());
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-teal-900/20 to-cyan-900/20 border border-teal-500/30 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Lightbulb className="w-6 h-6 text-teal-400 flex-shrink-0 mt-1" />
          <p className="text-slate-200">{block.content || 'Take a moment to reflect on what you learned.'}</p>
        </div>
      </div>
      
      <div className="space-y-4">
        {questions.map((question, idx) => (
          <div key={idx} className="bg-slate-800/30 rounded-xl p-4">
            <label className="text-sm text-teal-300 block mb-2">{question}</label>
            <textarea
              value={responses[idx] || ''}
              onChange={(e) => updateResponse(idx, e.target.value)}
              rows={2}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:border-teal-500/50 transition-all"
              placeholder="Share your thoughts..."
            />
          </div>
        ))}
      </div>
      
      <button
        onClick={() => {
          toast.success('Thoughtful reflection!');
          onComplete && onComplete(true, { responses });
        }}
        disabled={!allAnswered}
        className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
      >
        Complete Reflection
      </button>
    </div>
  );
};

// =============== TRUE/FALSE INTERACTION ===============
export const TrueFalse = ({ block, onComplete }) => {
  const [answer, setAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  
  const correctAnswer = block.correct_answer === 'true' || block.correct_answer === true;
  
  const handleCheck = () => {
    setShowResult(true);
    if (answer === correctAnswer) {
      toast.success('Correct!');
      onComplete && onComplete(true);
    }
  };
  
  return (
    <div className="space-y-6">
      <p className="text-lg text-slate-200 mb-4">{block.content}</p>
      
      <div className="flex gap-4">
        <button
          onClick={() => !showResult && setAnswer(true)}
          className={`flex-1 p-6 rounded-xl text-xl font-bold transition-all ${
            showResult && correctAnswer === true
              ? 'bg-green-500/30 border-2 border-green-500'
              : showResult && answer === true && !correctAnswer
                ? 'bg-red-500/30 border-2 border-red-500'
                : answer === true
                  ? 'bg-indigo-500/30 border-2 border-indigo-400'
                  : 'bg-slate-800/50 border-2 border-slate-700 hover:border-green-500/50'
          }`}
        >
          <span className="text-green-400">TRUE</span>
        </button>
        
        <button
          onClick={() => !showResult && setAnswer(false)}
          className={`flex-1 p-6 rounded-xl text-xl font-bold transition-all ${
            showResult && correctAnswer === false
              ? 'bg-green-500/30 border-2 border-green-500'
              : showResult && answer === false && correctAnswer
                ? 'bg-red-500/30 border-2 border-red-500'
                : answer === false
                  ? 'bg-indigo-500/30 border-2 border-indigo-400'
                  : 'bg-slate-800/50 border-2 border-slate-700 hover:border-red-500/50'
          }`}
        >
          <span className="text-red-400">FALSE</span>
        </button>
      </div>
      
      {!showResult && (
        <button
          onClick={handleCheck}
          disabled={answer === null}
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50"
        >
          Check Answer
        </button>
      )}
      
      {showResult && answer !== correctAnswer && (
        <button
          onClick={() => { setShowResult(false); setAnswer(null); }}
          className="w-full bg-slate-700 text-white rounded-full px-6 py-3 font-semibold hover:bg-slate-600 transition-all"
        >
          Try Again
        </button>
      )}
    </div>
  );
};

// =============== STORY/NARRATION DISPLAY ===============
export const StoryNarration = ({ block, onComplete }) => {
  return (
    <div className="space-y-6">
      {block.arethera_intro && (
        <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 rounded-xl p-6 mb-4">
          <p className="text-indigo-200 italic">"{block.arethera_intro}"</p>
          <p className="text-sm text-indigo-400 mt-2">— Aret'Hera</p>
        </div>
      )}
      
      <div className="prose prose-invert max-w-none">
        <p className="text-lg text-slate-200 leading-relaxed whitespace-pre-wrap">
          {block.content}
        </p>
      </div>
      
      <button
        onClick={() => onComplete && onComplete(true)}
        className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-6 py-3 font-semibold hover:opacity-90 transition-all"
      >
        Continue
      </button>
    </div>
  );
};

export default {
  MatchingPairs,
  DragDropOrder,
  SortingActivity,
  FillInBlank,
  QuickRecall,
  LogicPuzzle,
  CreativeWriting,
  DrawingActivity,
  MusicRhythm,
  ReflectionActivity,
  TrueFalse,
  StoryNarration
};
